import { useEffect, useState, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, Search, KeyRound, X, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Autocomplete picker for WineIdea software licenses.
 * Used inside the New Ticket dialog when intervention_type == "software" (required)
 * and inside TicketDetail sidebar to change the linked license.
 *
 * Props:
 *  - value: license object currently selected (or null)
 *  - onChange: (license|null) => void  — called when user picks or clears
 *  - required: bool — visual hint (does not enforce)
 *  - placeholder: string
 */
export function LicensePicker({ value, onChange, required = false, placeholder = "Cerca licenza (negozio, azienda, codice)..." }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/software-licenses", { params: query.trim() ? { q: query.trim() } : {} });
        if (!cancelled) setResults(data.slice(0, 15));
      } catch (e) { if (!cancelled) toast.error(formatApiError(e)); }
      finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open]);

  const pick = useCallback((lic) => {
    onChange(lic);
    setOpen(false);
    setQuery("");
  }, [onChange]);

  const clear = () => onChange(null);

  if (value) {
    return (
      <div className="rounded-sm border border-violet-200 bg-violet-50 px-3 py-2 flex items-start gap-2" data-testid="license-picker-selected">
        <KeyRound className="h-4 w-4 text-violet-700 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-900 flex items-center gap-2">
            <span className="truncate">{value.negozio}</span>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
          </div>
          <div className="text-[11px] text-zinc-600 truncate">
            {value.azienda ? `${value.azienda} · ` : ""}
            <span className="font-mono text-violet-800">{value.codice_assistenza}</span>
            {value.tipo_licenza ? ` · ${value.tipo_licenza}` : ""}
            {value.rif_ordine ? ` · Ord: ${value.rif_ordine}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={clear}
          data-testid="license-picker-clear"
          className="text-zinc-500 hover:text-zinc-800 shrink-0"
          title="Rimuovi licenza"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative" data-testid="license-picker">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
        <Input
          data-testid="license-picker-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className={`pl-8 rounded-sm ${required ? "border-violet-300" : ""}`}
        />
      </div>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-sm border border-zinc-200 bg-white shadow-lg"
          data-testid="license-picker-dropdown"
        >
          {loading && (
            <div className="p-3 text-center text-xs text-zinc-500"><Loader2 className="inline h-3 w-3 mr-1 animate-spin" /> Caricamento...</div>
          )}
          {!loading && results.length === 0 && (
            <div className="p-3 text-center text-xs text-zinc-500">Nessuna licenza trovata.</div>
          )}
          {!loading && results.map((lic) => (
            <button
              key={lic.id}
              type="button"
              onClick={() => pick(lic)}
              data-testid={`license-picker-option-${lic.id}`}
              className="w-full text-left px-3 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-violet-50"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{lic.negozio}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {lic.azienda ? `${lic.azienda} · ` : ""}
                    {lic.tipo_licenza || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-[11px] text-violet-800">{lic.codice_assistenza}</div>
                  {lic.rif_ordine && <div className="font-mono text-[10px] text-zinc-500">Ord: {lic.rif_ordine}</div>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
